import {
	terminalLog,
	debugLog,
	sendToTerminal,
} from "../../../lib/terminalLogger";
import { api, APIError } from "../../../lib/api";
import { ErrorCode } from "../../../lib/errors";
import {
	normalizeContraction,
	contractionToNumber,
} from "../../../utils/contractionNormalizer";
import { useNotifications } from "../../feedback/NotificationProvider";
import { ErrorCatalog } from "../../feedback/ErrorCatalog";

export interface ExportApiObjetoPayload {
	objeto_id: string | number;
	guid?: string | null;
	nome: string;
	quantidade: number;
	status: string;
	nome_arquivo_exportado: string;
	caminho_exportado: string;
	tamanho_arquivo: number;
	md5_arquivo?: string | null;
	duration_ms?: number | null;
	multiplicador?: number;
	contracao?: number;
	horario_entrega?: string;
}

export interface ExportApiPayload {
	orc_id: number;
	oit_id: number;
	tipo_exportacao: "PNG";
	diretorio_destino: string;
	diretorio_solicitado?: string;
	escala: number;
	transparencia: boolean;
	resolucao_dpi: number;
	compressao?: string;
	icc_profile?: string;
	objetos: ExportApiObjetoPayload[];
}

/** Normaliza HH:mm a partir de string possivelmente com segundos. */
function normalizeToHHmm(timeStr: string): string {
	if (!timeStr || typeof timeStr !== "string") return "18:00";
	const parts = timeStr.split(":");
	if (parts.length >= 2) {
		const hh = parts[0].padStart(2, "0");
		const mm = parts[1].padStart(2, "0");
		return `${hh}:${mm}`;
	}
	return timeStr;
}

/**
 * Gera o payload para /arquivos/exportarPNG. Reproduz a lógica do componente atual.
 */
export function buildExportPayload(
	currentSaveData: any,
	diretorioEfetivo?: string,
	exportResult?: {
		objetos?: Array<any>;
		exportados?: Array<any>;
		diretorioSolicitado?: string;
	},
): ExportApiPayload | null {
	try {
		const savedData = currentSaveData;
		const pedidoId = savedData.pedido?.orc_id || "0";
		const itemId = savedData.pedido?.oit_id || "0";

		// Diretório de destino
		let diretorioDestino: string;
		if (diretorioEfetivo) {
			diretorioDestino = diretorioEfetivo;
		} else {
			const date = new Date();
			const ano = date.getFullYear();
			const mesesNomes = [
				"01-JANEIRO",
				"02-FEVEREIRO",
				"03-MARÇO",
				"04-ABRIL",
				"05-MAIO",
				"06-JUNHO",
				"07-JULHO",
				"08-AGOSTO",
				"09-SETEMBRO",
				"10-OUTUBRO",
				"11-NOVEMBRO",
				"12-DEZEMBRO",
			];
			const mes = mesesNomes[date.getMonth()];
			// Formato de data: dd-mm (sem ano na pasta, conforme padrão v3.0)
			const dd = date.getDate().toString().padStart(2, "0");
			const mm = (date.getMonth() + 1).toString().padStart(2, "0");
			const dataFormatada = `${dd}-${mm}`;

			if (savedData.regras?.diretorio_base_exportpng) {
				const base = savedData.regras.diretorio_base_exportpng;

				// Normalização padronizada (mesma do dual export)
				const sanitizeNome = (s: string) =>
					(s || "Indefinido")
						.toUpperCase()
						.replace(/[<>:"/\\|?*]/g, "_")
						.trim();

				// Cliente
				const cliId = savedData.cliente?.cli_id || savedData.cliente?.id || "0";
				const cliNomeRaw =
					savedData.cliente?.nome_fantasia ||
					savedData.cliente?.nome ||
					"Cliente";
				const cliNome = sanitizeNome(cliNomeRaw);
				const pastaCliente = `${cliId}_${cliNome}`;

				// Material ou Produto
				const materialRaw =
					savedData.material?.nome || savedData.produto?.nome || "Material";
				const material = sanitizeNome(materialRaw);

				// ✅ Nova Estrutura Padronizada: Base\CLIENTES\Cliente\Ano\Mês\Data\Material\oitId\layout
				// Removido orcId (pedidoId) conforme solicitado para padronização Flat
				diretorioDestino = `${base}\\CLIENTES\\${pastaCliente}\\${ano}\\${mes}\\${dataFormatada}\\${material}\\${itemId}\\layout`;
			} else {
				terminalLog.error(
					"❌",
					"Diretório de exportação PNG não encontrado nas regras do produto. Verifique a configuração da API.",
				);
				return null;
			}
		}
		// Removidos logs verborrágicos; manteremos logs no envio efetivo

		const contracaoGlobal = savedData.configuracoes?.contracao || "0px";
		const horarioEntrega = savedData.configuracoes?.entrega || "Hoje";
		const horarioCorteOriginal = savedData.regras?.horario_corte || "18:00";
		const horarioCorte = normalizeToHHmm(horarioCorteOriginal);

		// Mapa de resultados do C# por staticId e por nome para enriquecer status/caminho/tamanho
		const resultByStaticId: Record<string, any> = {};
		const resultByNome: Record<string, any> = {};
		const resultList: any[] = Array.isArray(exportResult?.exportados)
			? exportResult!.exportados!
			: Array.isArray(exportResult?.objetos)
				? exportResult!.objetos!
				: [];
		if (Array.isArray(resultList)) {
			resultList.forEach((o: any) => {
				const sid = o?.staticId != null ? String(o.staticId) : undefined;
				if (sid) resultByStaticId[sid] = o;
				if (o?.nome) resultByNome[String(o.nome)] = o;
			});
		}

		const objetos = (savedData.objetos || []).map((obj: any, index: number) => {
			let contracaoNumero = 0;
			// Normalizar contração antes do cálculo para remover "n/a" e barras
			const normalizedContraction = obj.contraction
				? normalizeContraction(obj.contraction)
				: null;
			const normalizedGlobal =
				contracaoGlobal &&
				contracaoGlobal !== "CV" &&
				contracaoGlobal !== "VARIÁVEL"
					? normalizeContraction(contracaoGlobal)
					: null;

			if (normalizedContraction) {
				contracaoNumero = contractionToNumber(normalizedContraction);
			} else if (normalizedGlobal) {
				contracaoNumero = contractionToNumber(normalizedGlobal);
			}

			let horarioEntregaFinal = "18:00";
			const entregaUpper = String(horarioEntrega).toUpperCase();
			if (entregaUpper === "HOJE") horarioEntregaFinal = horarioCorte;
			else if (entregaUpper === "AMANHÃ" || entregaUpper === "AMANHA")
				horarioEntregaFinal = "09:00";
			else if (entregaUpper === "NOTURNA") horarioEntregaFinal = "22:00";
			else if (entregaUpper === "URGENTE") horarioEntregaFinal = "12:00";

			const nomeArquivoExportadoPadrao = `${
				obj.nome || `obj-${index + 1}`
			}.png`;
			const caminhoExportadoPadrao = `${diretorioDestino}\\${nomeArquivoExportadoPadrao}`;

			// Enriquecer com resultado do C# se existir
			const res =
				(obj.staticId && resultByStaticId[String(obj.staticId)]) ||
				(obj.nome && resultByNome[String(obj.nome)]) ||
				null;
			const statusFinal =
				res?.status === "ok"
					? "success"
					: res?.status === "erro"
						? "error"
						: "success";
			const caminhoFinal =
				typeof res?.caminho === "string" && res.caminho.length > 0
					? res.caminho
					: caminhoExportadoPadrao;
			const tamanhoFinal =
				typeof res?.tamanho === "number"
					? res.tamanho
					: typeof res?.size === "number"
						? res.size
						: 0;
			const md5Final =
				res && typeof res.md5 !== "undefined"
					? (res.md5 as string | null)
					: undefined;
			const durationMsFinal =
				typeof res?.durationMs === "number" ? res.durationMs : null;
			const guidFinal = res?.guid ?? null;
			const pageFinal = typeof res?.page === "number" ? res.page : undefined;
			const errorFinal =
				typeof res?.error === "string" && res.error ? res.error : undefined;
			const nomeArquivoFinal = (() => {
				// Preferir o mesmo nome enviado ao C# via output_name, quando retornado
				if (
					typeof res?.nome_arquivo_exportado === "string" &&
					res.nome_arquivo_exportado.trim().length > 0
				) {
					return res.nome_arquivo_exportado.trim();
				}
				if (
					typeof res?.output_name === "string" &&
					res.output_name.trim().length > 0
				) {
					const nm = res.output_name.trim();
					return nm.toUpperCase().endsWith(".PNG") ? nm : `${nm}.png`;
				}
				try {
					return caminhoFinal.split("\\").pop() || nomeArquivoExportadoPadrao;
				} catch {
					return nomeArquivoExportadoPadrao;
				}
			})();

			return {
				objeto_id: res?.staticId ?? obj.id ?? `obj-${index + 1}`,
				guid: guidFinal,
				nome: obj.nome || `Objeto ${index + 1}`,
				quantidade: parseInt(obj.quantity) || 1,
				status: statusFinal,
				nome_arquivo_exportado: nomeArquivoFinal,
				caminho_exportado: caminhoFinal,
				tamanho_arquivo: tamanhoFinal,
				...(typeof md5Final !== "undefined" ? { md5_arquivo: md5Final } : {}),
				...(durationMsFinal != null ? { duration_ms: durationMsFinal } : {}),
				...(typeof pageFinal !== "undefined" ? { page: pageFinal } : {}),
				...(typeof errorFinal !== "undefined" ? { error: errorFinal } : {}),
				multiplicador: parseInt(obj.quantity) || 1,
				contracao: contracaoNumero,
				horario_entrega: horarioEntregaFinal,
			} as ExportApiObjetoPayload;
		});

		return {
			orc_id: parseInt(pedidoId, 10) || 0,
			oit_id: parseInt(itemId, 10) || 0,
			tipo_exportacao: "PNG",
			diretorio_destino: diretorioDestino,
			...(exportResult?.diretorioSolicitado
				? { diretorio_solicitado: exportResult?.diretorioSolicitado }
				: {}),
			escala: 0.1,
			transparencia: true,
			resolucao_dpi:
				(savedData.regras?.exportacao?.resolucao_dpi as number) || 300,
			compressao: savedData.regras?.exportacao?.compressao,
			icc_profile: savedData.regras?.exportacao?.icc_profile,
			objetos,
		};
	} catch (error) {
		terminalLog.error(
			"❌ Erro ao gerar payload para API de exportação:",
			error,
		);
		return null;
	}
}

function validateExportPngPayload(payload: ExportApiPayload): {
	valid: boolean;
	error?: string;
} {
	if (!payload || typeof payload !== "object")
		return { valid: false, error: "Payload ausente" };
	if (!Number.isInteger(payload.orc_id))
		return { valid: false, error: "orc_id inválido (deve ser inteiro)" };
	if (!Number.isInteger(payload.oit_id))
		return { valid: false, error: "oit_id inválido (deve ser inteiro)" };
	if (
		!payload.diretorio_destino ||
		typeof payload.diretorio_destino !== "string"
	)
		return { valid: false, error: "diretorio_destino é obrigatório" };
	if (!Array.isArray(payload.objetos) || payload.objetos.length === 0)
		return { valid: false, error: "objetos deve conter pelo menos 1 item" };
	// Restrições opcionais quando presentes
	if (
		typeof payload.escala === "number" &&
		(payload.escala < 0.1 || payload.escala > 10)
	)
		return {
			valid: false,
			error: "escala fora do intervalo permitido (0.1–10)",
		};
	if (
		typeof payload.resolucao_dpi === "number" &&
		(payload.resolucao_dpi < 72 || payload.resolucao_dpi > 600)
	)
		return {
			valid: false,
			error: "resolucao_dpi fora do intervalo permitido (72–600)",
		};
	for (const [idx, obj] of payload.objetos.entries()) {
		if (
			obj.objeto_id === undefined ||
			obj.objeto_id === null ||
			obj.objeto_id === ""
		)
			return { valid: false, error: `objetos[${idx}].objeto_id é obrigatório` };
		if (!obj.nome || typeof obj.nome !== "string")
			return { valid: false, error: `objetos[${idx}].nome é obrigatório` };
		if (!Number.isInteger(obj.quantidade) || obj.quantidade < 1)
			return { valid: false, error: `objetos[${idx}].quantidade inválida` };
		if (
			typeof obj.contracao === "number" &&
			(obj.contracao < 0 || obj.contracao > 100)
		)
			return {
				valid: false,
				error: `objetos[${idx}].contracao fora do intervalo (0–100)`,
			};
	}
	return { valid: true };
}

export async function postExport(
	payload: ExportApiPayload,
	_token: string,
	idempotencyKey?: string,
): Promise<{
	success: boolean;
	message?: string;
	data?: any;
	status?: number;
	details?: any;
}> {
	try {
		// Validação local antes do envio
		const validation = validateExportPngPayload(payload);
		if (!validation.valid) {
			return {
				success: false,
				message: validation.error || "Payload inválido",
			};
		}
		const idempotency =
			idempotencyKey ||
			((window as any)?.crypto?.randomUUID
				? (window as any).crypto.randomUUID()
				: undefined);
		const data: any = await api("/arquivos/exportarPNG", {
			method: "POST",
			headers: {
				...(idempotency ? { "Idempotency-Key": idempotency } : {}),
			},
			body: JSON.stringify(payload),
		});
		// Estrutura esperada pela documentação: { ok: true, result: {...} }
		// Estrutura atual da API: { success: false, data: { ok: true, result: {...} } }
		// Abordagem defensiva: priorizar documentação, mas funcionar com estrutura atual

		// Se a API segue a documentação (estrutura simples)
		if (data?.ok === true) {
			return { success: true, data };
		}

		// Se a API usa estrutura aninhada (bug atual)
		const nestedData = data?.data;
		const okFlag = Boolean(
			nestedData?.ok === true ||
			nestedData?.result?.layout_export_id ||
			nestedData?.result?.id,
		);
		return { success: okFlag, data };
	} catch (error: any) {
		if (error instanceof APIError) {
			const code = (error.data?.code || "").toString();
			const catalogEntry = (ErrorCatalog as any)[code as ErrorCode];
			const message = catalogEntry
				? typeof catalogEntry.message === "function"
					? catalogEntry.message({})
					: catalogEntry.message
				: error.data?.message ||
					error.message ||
					"Erro na API de exportação (PNG)";
			try {
				sendToTerminal("❌ [EXPORT_API] Erro ao exportar PNG", {
					status: error.status,
					message,
					data: error.data,
				});
			} catch {}
			return {
				success: false,
				message,
				status: error.status,
				details: error.data,
			};
		}
		try {
			sendToTerminal("❌ [EXPORT_API] Erro desconhecido ao exportar PNG", {
				error: error?.message || String(error),
			});
		} catch {}
		return {
			success: false,
			message:
				error?.message || "Erro ao se comunicar com o servidor de exportação",
		};
	}
}
